import React, { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { subscribeTrophyNotifications } from '../../utils/trophyManager';
import type { TrophyType } from '../../types/trophy';

const trophyDetails: Record<TrophyType, { title: string; description: string }> = {
  first_submission: {
    title: 'First Submission',
    description: 'You submitted your first animatronic data!'
  },
  ten_submissions: {
    title: 'Data Contributor',
    description: 'You\'ve submitted 10 animatronic entries!'
  },
  first_comment: {
    title: 'First Comment',
    description: 'You posted your first comment in the forum!'
  },
  popular_submission: {
    title: 'Popular Submission',
    description: 'One of your submissions reached 100 views!'
  },
  helpful_contributor: {
    title: 'Helpful Contributor',
    description: 'You received 10 helpful votes on your comments!'
  }
};

const TrophyNotification = () => {
  const [notifications, setNotifications] = useState<TrophyType[]>([]);

  useEffect(() => {
    const unsubscribe = subscribeTrophyNotifications((trophies) => {
      setNotifications(prev => [...prev, ...trophies]);
    });

    return () => unsubscribe();
  }, []);

  const removeNotification = (index: number) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {notifications.map((type, index) => (
          <motion.div
            key={`${type}-${index}`}
            initial={{ opacity: 0, y: 50, scale: 0.3 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
            className="bg-background-secondary border border-brand/50 rounded-lg p-4 shadow-lg flex items-start gap-3 max-w-sm"
          >
            <div className="p-2 bg-brand/10 rounded-lg">
              <Trophy className="w-6 h-6 text-brand" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-content-primary">
                {trophyDetails[type].title}
              </h3>
              <p className="text-sm text-content-secondary">
                {trophyDetails[type].description}
              </p>
            </div>
            <button
              onClick={() => removeNotification(index)}
              className="text-content-secondary hover:text-content-primary"
            >
              ×
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default TrophyNotification;